import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";
import api from "@/api";
import Seo, { absoluteUrl, breadcrumbSchema, webPageSchema } from "@/components/Seo";

const coverSrc = (cover) => (
  cover && (cover.startsWith("http") || cover.startsWith("/"))
    ? cover
    : `${process.env.REACT_APP_BACKEND_URL}/api/files/${cover}`
);

const Journal = () => {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/posts").then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="journal-page">
      <Seo
        title="Chai Journal | Stories on Kulhad Chai & Tea Culture | Chai Times"
        description="Read Chai Times stories about kulhad chai, kadak brewing, tea gardens, café culture and the brand's Bengaluru journey."
        path="/journal"
        image="/pour-hero.webp"
        schema={[
          webPageSchema({
            type: "Blog",
            name: "Chai Times Journal",
            description: "Stories about kulhad chai, kadak brewing, tea gardens and café culture from Chai Times Bengaluru.",
            path: "/journal",
          }),
          {
            "@type": "ItemList",
            itemListElement: posts.map((post, index) => ({
              "@type": "ListItem",
              position: index + 1,
              url: absoluteUrl(`/journal/${post.slug}`),
              name: post.title,
            })),
          },
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Journal", path: "/journal" }]),
        ]}
      />
      <section className="pt-36 md:pt-44 pb-14 max-w-7xl mx-auto px-5 md:px-10">
        <Reveal>
          <SectionTag>The Journal</SectionTag>
          <h1 className="font-serif font-black text-5xl md:text-7xl tracking-tight mt-5 leading-[0.98]">
            Stories from<br /><span className="italic text-terracotta">the kettle.</span>
          </h1>
        </Reveal>
      </section>

      <EditorialMarquee text="Notes on chai, clay & conversation" className="bg-sand text-charcoal" />

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20 space-y-16">
        {posts.map((p, i) => (
          <Reveal key={p.id} delay={0.05}>
            <Link
              to={`/journal/${p.slug}`}
              data-testid={`journal-card-${p.slug}`}
              className={`group grid md:grid-cols-12 gap-8 items-center ${i % 2 ? "" : ""}`}
            >
              <div className={`md:col-span-6 ${i % 2 ? "md:order-2" : ""}`}>
                <div className="rounded-2xl overflow-hidden aspect-[16/10]">
                  <img src={coverSrc(p.cover)} alt={`${p.title} — Chai Times Journal`} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
              </div>
              <div className={`md:col-span-6 ${i % 2 ? "md:order-1" : ""}`}>
                <div className="flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.2em] text-terracotta">
                  <span>{p.tag}</span><span className="text-charcoal/40">{p.date}</span>
                </div>
                <h2 className="font-serif font-bold text-3xl md:text-4xl mt-4 mb-4 group-hover:text-terracotta transition-colors">{p.title}</h2>
                <p className="text-charcoal/65 leading-relaxed max-w-lg">{p.excerpt}</p>
                <span className="inline-flex items-center gap-2 mt-6 font-mono text-xs uppercase tracking-[0.2em] text-charcoal group-hover:text-terracotta transition-colors">
                  Read the story <ArrowUpRight size={14} />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </section>
    </div>
  );
};

export default Journal;
